import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardTitle, Input } from 'reactstrap';
import { projetsAPI } from '../../services/api';
import './GanttChart.css';

const GanttChart = () => {
    const [projets, setProjets] = useState([]);
    const [selectedProjet, setSelectedProjet] = useState(null);
    const [etapes, setEtapes] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchProjets();
    }, []);

    useEffect(() => {
        if (selectedProjet) {
            fetchProjetDetails(selectedProjet);
        }
    }, [selectedProjet]);

    const fetchProjets = async () => {
        try {
            const res = await projetsAPI.getAll();
            const projectList = Array.isArray(res.data) ? res.data : [];
            setProjets(projectList);

            if (projectList.length > 0) {
                setSelectedProjet(projectList[0].id);
            }
        } catch (error) {
            console.error('Erreur chargement projets', error);
            setProjets([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchProjetDetails = async (projetId) => {
        try {
            const res = await projetsAPI.getById(projetId);
            const projet = res.data;

            const etapesList = Array.isArray(projet.etapes) ? projet.etapes : [];
            setEtapes(etapesList.sort((a, b) => a.ordre - b.ordre));

            const ticketsList = Array.isArray(projet.tickets) ? projet.tickets : [];
            setTickets(ticketsList);
        } catch (error) {
            console.error('Erreur chargement détails projet', error);
            setEtapes([]);
            setTickets([]);
        }
    };

    const getTicketsForEtape = (etapeId) => {
        return tickets.filter(t => t.etape_id === etapeId);
    };

    const getOrphanTickets = () => {
        // Récupérer les IDs de toutes les étapes
        const etapeIds = etapes.map(e => e.id);
        // Tickets sans etape_id ou avec un etape_id qui n'existe pas
        return tickets.filter(t => !t.etape_id || !etapeIds.includes(t.etape_id));
    };

    // Générer la timeline (mois et semaines)
    const generateTimeline = () => {
        if (etapes.length === 0) return { months: [], totalWeeks: 0 };

        const dates = [];
        etapes.forEach(e => {
            if (e.date_debut) dates.push(new Date(e.date_debut));
            if (e.date_fin) dates.push(new Date(e.date_fin));
        });

        tickets.forEach(t => {
            if (t.deadline) dates.push(new Date(t.deadline));
        });

        if (dates.length === 0) return { months: [], totalWeeks: 0 };

        const minDate = new Date(Math.min(...dates));
        const maxDate = new Date(Math.max(...dates));

        // Arrondir au début du mois
        minDate.setDate(1);

        // Arrondir à la fin du mois
        maxDate.setMonth(maxDate.getMonth() + 1);
        maxDate.setDate(0);

        const months = [];
        let currentDate = new Date(minDate);
        let weekCounter = 0;

        while (currentDate <= maxDate) {
            const monthStart = new Date(currentDate);
            const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

            // Calculer le nombre de semaines dans ce mois
            const weeks = Math.ceil((monthEnd.getDate() - monthStart.getDate() + 1) / 7);

            months.push({
                name: monthStart.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
                weeks: weeks,
                startWeek: weekCounter
            });

            weekCounter += weeks;
            currentDate.setMonth(currentDate.getMonth() + 1);
        }

        return { months, totalWeeks: weekCounter, minDate, maxDate };
    };

    const calculateBarPosition = (dateDebut, dateFin, minDate, maxDate) => {
        if (!dateDebut || !minDate || !maxDate) return { left: 0, width: 0 };

        const totalDuration = maxDate - minDate;
        const start = new Date(dateDebut);
        const end = dateFin ? new Date(dateFin) : new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000); // 1 semaine par défaut

        const offsetStart = start - minDate;
        const duration = end - start;

        const left = (offsetStart / totalDuration) * 100;
        const width = (duration / totalDuration) * 100;

        return { left: Math.max(0, left), width: Math.max(2, width) };
    };

    const getTicketColor = (index) => {
        const colors = [
            '#FF9F40', // Orange
            '#FFCD56', // Jaune
            '#FF6384', // Rose
            '#36A2EB', // Bleu
            '#4BC0C0', // Cyan
            '#9966FF', // Violet
            '#FF9966', // Orange clair
            '#66B3FF'  // Bleu clair
        ];
        return colors[index % colors.length];
    };

    const timeline = generateTimeline();
    const orphanTickets = getOrphanTickets();

    if (loading) {
        return (
            <Card>
                <CardBody className="text-center p-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-2">Chargement...</p>
                </CardBody>
            </Card>
        );
    }

    if (projets.length === 0) {
        return (
            <Card>
                <CardBody className="text-center p-5">
                    <i className="mdi mdi-chart-gantt text-muted" style={{ fontSize: '48px' }}></i>
                    <p className="text-muted mt-3">Aucun projet disponible</p>
                </CardBody>
            </Card>
        );
    }

    return (
        <Card>
            <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <CardTitle className="h4 mb-0">
                        <i className="mdi mdi-chart-gantt me-2"></i>
                        Diagramme de Gantt
                    </CardTitle>
                    <Input
                        type="select"
                        value={selectedProjet || ''}
                        onChange={(e) => setSelectedProjet(Number(e.target.value))}
                        style={{ width: '300px' }}
                    >
                        {projets.map(p => (
                            <option key={p.id} value={p.id}>{p.nom}</option>
                        ))}
                    </Input>
                </div>

                {etapes.length === 0 || timeline.months.length === 0 ? (
                    <div className="text-center p-5">
                        <i className="mdi mdi-calendar-blank text-muted" style={{ fontSize: '48px' }}></i>
                        <p className="text-muted mt-3">
                            Aucune étape avec des dates définies pour ce projet.<br />
                            <small>Ajoutez des dates de début et de fin à vos étapes pour voir le planning.</small>
                        </p>
                    </div>
                ) : (
                    <div className="gantt-chart-modern">
                        {/* Timeline Header */}
                        <div className="gantt-modern-header">
                            <div className="gantt-modern-sidebar-header">
                                <div className="fw-bold">GESTION DE PROJET</div>
                            </div>
                            <div className="gantt-modern-timeline-header">
                                {timeline.months.map((month, i) => (
                                    <div key={i} className="gantt-month-header" style={{ gridColumn: `span ${month.weeks}` }}>
                                        <div className="month-name">{month.name}</div>
                                        <div className="week-numbers">
                                            {[...Array(month.weeks)].map((_, w) => (
                                                <div key={w} className="week-cell">S{w + 1}</div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Gantt Body */}
                        <div className="gantt-modern-body">
                            {etapes.map((etape, etapeIndex) => {
                                const etapeTickets = getTicketsForEtape(etape.id);

                                return (
                                    <div key={etape.id} className="gantt-section">
                                        {/* Section Header (Étape) */}
                                        <div className="gantt-modern-row gantt-section-header">
                                            <div className="gantt-modern-sidebar">
                                                <div className="section-title">
                                                    {etape.nom}
                                                    <small className="ms-2 text-muted fw-normal">
                                                        ({etapeTickets.length} ticket{etapeTickets.length > 1 ? 's' : ''})
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="gantt-modern-timeline" style={{ gridTemplateColumns: `repeat(${timeline.totalWeeks}, 1fr)` }}>
                                                {etape.date_debut && etape.date_fin && (
                                                    <div
                                                        className="gantt-bar-modern gantt-bar-etape"
                                                        style={{
                                                            ...calculateBarPosition(etape.date_debut, etape.date_fin, timeline.minDate, timeline.maxDate),
                                                            position: 'absolute',
                                                            height: '8px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            backgroundColor: '#34495e',
                                                            borderRadius: '4px',
                                                            opacity: 0.3
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        {/* Tickets de l'étape */}
                                        {etapeTickets.length > 0 ? (
                                            etapeTickets.map((ticket, ticketIndex) => {
                                                const barPos = ticket.deadline
                                                    ? calculateBarPosition(
                                                        ticket.created_at || ticket.deadline,
                                                        ticket.deadline,
                                                        timeline.minDate,
                                                        timeline.maxDate
                                                    )
                                                    : { left: 0, width: 0 };

                                                return (
                                                    <div key={ticket.id} className="gantt-modern-row">
                                                        <div className="gantt-modern-sidebar">
                                                            <div className="task-name">
                                                                <i className="mdi mdi-ticket me-2"></i>
                                                                #{ticket.id} - {ticket.titre}
                                                            </div>
                                                        </div>
                                                        <div className="gantt-modern-timeline" style={{ gridTemplateColumns: `repeat(${timeline.totalWeeks}, 1fr)` }}>
                                                            {ticket.deadline ? (
                                                                <div
                                                                    className="gantt-bar-modern"
                                                                    style={{
                                                                        left: `${barPos.left}%`,
                                                                        width: `${barPos.width}%`,
                                                                        backgroundColor: getTicketColor(etapeIndex * 10 + ticketIndex)
                                                                    }}
                                                                >
                                                                    <span className="gantt-bar-label-modern">
                                                                        {ticket.assigned_to?.name || 'Non assigné'}
                                                                    </span>
                                                                    <div className="gantt-tooltip-modern">
                                                                        <strong>{ticket.titre}</strong><br />
                                                                        Assigné: {ticket.assigned_to?.name || 'Non assigné'}<br />
                                                                        Échéance: {new Date(ticket.deadline).toLocaleDateString()}<br />
                                                                        Statut: {ticket.statut}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="d-flex align-items-center ps-2">
                                                                    <span className="badge bg-secondary">Sans échéance</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="gantt-modern-row">
                                                <div className="gantt-modern-sidebar">
                                                    <div className="task-name text-muted">
                                                        <i className="mdi mdi-information-outline me-2"></i>
                                                        Aucun ticket assigné à cette étape
                                                    </div>
                                                </div>
                                                <div className="gantt-modern-timeline" style={{ gridTemplateColumns: `repeat(${timeline.totalWeeks}, 1fr)` }}>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Section des tickets orphelins (sans étape) */}
                            {orphanTickets.length > 0 && (
                                <div className="gantt-section">
                                    <div className="gantt-modern-row gantt-section-header" style={{ borderLeft: '3px solid #f39c12' }}>
                                        <div className="gantt-modern-sidebar">
                                            <div className="section-title">
                                                <i className="mdi mdi-alert-circle-outline me-2"></i>
                                                Tickets non assignés à une étape
                                                <small className="ms-2 text-muted fw-normal">
                                                    ({orphanTickets.length} ticket{orphanTickets.length > 1 ? 's' : ''})
                                                </small>
                                            </div>
                                        </div>
                                        <div className="gantt-modern-timeline" style={{ gridTemplateColumns: `repeat(${timeline.totalWeeks}, 1fr)` }}>
                                        </div>
                                    </div>

                                    {orphanTickets.map((ticket, ticketIndex) => {
                                        const barPos = ticket.deadline
                                            ? calculateBarPosition(
                                                ticket.created_at || ticket.deadline,
                                                ticket.deadline,
                                                timeline.minDate,
                                                timeline.maxDate
                                            )
                                            : { left: 0, width: 0 };

                                        return (
                                            <div key={ticket.id} className="gantt-modern-row">
                                                <div className="gantt-modern-sidebar">
                                                    <div className="task-name">
                                                        <i className="mdi mdi-ticket me-2 text-warning"></i>
                                                        #{ticket.id} - {ticket.titre}
                                                    </div>
                                                </div>
                                                <div className="gantt-modern-timeline" style={{ gridTemplateColumns: `repeat(${timeline.totalWeeks}, 1fr)` }}>
                                                    {ticket.deadline ? (
                                                        <div
                                                            className="gantt-bar-modern"
                                                            style={{
                                                                left: `${barPos.left}%`,
                                                                width: `${barPos.width}%`,
                                                                backgroundColor: '#f39c12'
                                                            }}
                                                        >
                                                            <span className="gantt-bar-label-modern">
                                                                {ticket.assigned_to?.name || 'Non assigné'}
                                                            </span>
                                                            <div className="gantt-tooltip-modern">
                                                                <strong>{ticket.titre}</strong><br />
                                                                ⚠️ Non lié à une étape<br />
                                                                Assigné: {ticket.assigned_to?.name || 'Non assigné'}<br />
                                                                Échéance: {new Date(ticket.deadline).toLocaleDateString()}<br />
                                                                Statut: {ticket.statut}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="d-flex align-items-center ps-2">
                                                            <span className="badge bg-warning">Sans échéance</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Légende */}
                        <div className="gantt-legend-modern mt-3">
                            <small className="text-muted">
                                <i className="mdi mdi-information-outline me-1"></i>
                                {tickets.length} ticket{tickets.length > 1 ? 's' : ''} •
                                {etapes.length} étape{etapes.length > 1 ? 's' : ''} •
                                Timeline: {timeline.minDate?.toLocaleDateString()} - {timeline.maxDate?.toLocaleDateString()}
                                {orphanTickets.length > 0 && (
                                    <span className="text-warning ms-2">
                                        • ⚠️ {orphanTickets.length} ticket{orphanTickets.length > 1 ? 's' : ''} non assigné{orphanTickets.length > 1 ? 's' : ''}
                                    </span>
                                )}
                            </small>
                        </div>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default GanttChart;
